import { r as createLogger } from "../logger-CdXeOlrX.js";
import { gpsPathToCoverageCells } from "../geo/h3-proximity.js";
import { GPS_POINT_SOURCE_DEVICE, gpsPointSourceOf } from "gps-plus-slam-js";
//#region ../src/storage/session-metadata-record.ts
/**
* The session-metadata record (`session.json`) a finished recording leaves
* behind.
*
* WHY IT IS ITS OWN MODULE. It was forty-five lines inside the Recorder's
* `performStop`, which is one function inside `createRecordingSessionHandlers`
* - 717 lines carrying eight distinct concerns. This is the piece of that
* pipeline whose inputs are all VALUES: it closes over no mutable state of the
* enclosing factory, so it could move out without threading anything.
*
* WHY IT IS IN THE FRAMEWORK (2026-09-28). A second app writes recordings
* now: the Tour Viewer's troubleshooting recording, which must replay in the
* Recorder's desktop replay. The replay decides whether to migrate a
* recording from `odomCoordVersion`, so two writers of this record must agree
* on it - which is shared behaviour, and shared behaviour has one
* implementation (DEC-H3). The build info, which each app stamps at its own
* build, is injected rather than read here.
*
* WHAT IT DOES NOT DO: decide anything. It is a record builder plus the one
* write, so what a finished session claims about itself can be asserted
* directly rather than through a stop flow that also stops cameras.
*
* @see session-metadata-record.ts.md
*/
const log = createLogger("session-metadata");
/**
* Builds the record. Pure: no I/O, no clock, no store.
*
* **A MISSING `startTime` FALLS BACK TO `endTime`, and that is a known lie
* rather than a sensible default.** It means a session whose start was never
* recorded reports a duration of approximately zero. The caller logs an error
* when it happens, because the honest alternative - refusing to write metadata
* at all - would lose the recording's only self-description over a field that
* is missing precisely when something else has already gone wrong.
*/
function buildSessionMetadataRecord(input) {
	let build;
	try {
		build = input.getBuildInfo?.();
	} catch (error) {
		log.warn("Build metadata unavailable for session metadata", error);
	}
	const h3Cells = gpsPathToCoverageCells(input.gpsPositions.filter((p) => gpsPointSourceOf(p) === GPS_POINT_SOURCE_DEVICE).map((p) => ({
		lat: p.latitude,
		lng: p.longitude
	})));
	return {
		version: 1,
		odomCoordVersion: 5,
		startedAt: new Date(input.startTime ?? input.endTime).toISOString(),
		endedAt: new Date(input.endTime).toISOString(),
		contextTag: input.contextTag,
		actionCount: input.gpsPositions.length,
		frameCount: input.frameCount,
		userAgent: input.userAgent,
		...build ? { build } : {},
		...input.pageUrl === void 0 ? {} : { pageUrl: input.pageUrl },
		h3Cells,
		h3Resolution: 11
	};
}
/**
* The page url without its query and hash, for a record that leaves the
* device: a query can carry a private link (the Tour Viewer's `?qr=`).
* `undefined` without a url.
*/
function sanitizedPageUrl(href) {
	if (!href) return;
	try {
		const url = new URL(href);
		url.search = "";
		url.hash = "";
		return url.toString();
	} catch {
		const cutIndex = [href.indexOf("?"), href.indexOf("#")].filter((index) => index >= 0).sort((left, right) => left - right)[0];
		return cutIndex === void 0 ? href : href.slice(0, cutIndex);
	}
}
/**
* Writes the record, and never lets a failure take the stop flow down with it.
*
* The stop flow past this point ends the session, builds the summary and
* returns the user to a usable screen. A recording whose metadata failed to
* write is still a recording; a stop that threw here would leave the controls
* on screen with the button stuck busy.
*/
async function writeSessionMetadata(writer, input) {
	try {
		await writer(buildSessionMetadataRecord(input));
	} catch (err) {
		log.error("Failed to write session metadata:", err);
	}
}
//#endregion
export { buildSessionMetadataRecord, sanitizedPageUrl, writeSessionMetadata };
